// src/pages/DashboardList.jsx
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Empty, Spin, message, Modal, Input, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { dashboardStorage } from '../services/dashboardStorage';
import { useTheme } from '../context/ThemeContext';

const DashboardList = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');

  useEffect(() => {
    loadDashboards();
  }, []);

  const loadDashboards = async () => {
    setLoading(true);
    try {
      const list = await dashboardStorage.list();
      setDashboards(list);
    } catch (error) {
      message.error('Failed to load dashboards');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (dashboardName) => {
    navigate(`/graphs-test?dashboard=${encodeURIComponent(dashboardName)}`);
  };

  const handleCreateNew = () => {
    setCreateModalVisible(true);
  };

  const handleCreateConfirm = () => {
    if (!newDashboardName.trim()) {
      message.warning('Please enter a dashboard name');
      return;
    }
    setCreateModalVisible(false);
    navigate(`/graphs-test?dashboard=${encodeURIComponent(newDashboardName)}&new=true`);
  };

  const handleDeleteClick = (dashboardName, e) => {
    e.stopPropagation();
    setDashboardToDelete(dashboardName);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!dashboardToDelete) return;

    try {
      const result = await dashboardStorage.delete(dashboardToDelete);
      if (result.success) {
        message.success('Dashboard deleted');
        loadDashboards();
        setDeleteModalVisible(false);
        setDashboardToDelete(null);
      } else {
        message.error('Failed to delete dashboard');
      }
    } catch (error) {
      message.error('Error deleting dashboard');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: isDark ? '#e6edf3' : undefined }}>My Dashboards</h1>
          <p style={{ margin: '4px 0 0 0', color: isDark ? '#adbac7' : '#8c8c8c' }}>
            {dashboards.length} dashboard{dashboards.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          type="primary"size="large"
          onClick={handleCreateNew}
        >
          Create New Dashboard
        </Button>
      </div>

      {/* Dashboard Grid */}
      {dashboards.length === 0 ? (
        <Empty
          description="No dashboards yet"
          style={{ marginTop: '100px' }}
        >
          <Button type="primary"onClick={handleCreateNew}>
            Create Your First Dashboard
          </Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {dashboards.map((dashboard) => (
            <Col key={dashboard.name} xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                onClick={() => handleCardClick(dashboard.name)}
                bodyStyle={{ padding: '16px', background: isDark ? '#161b22' : undefined }}
                style={{ 
                  height: '100%',
                  borderRadius: '8px',
                  transition: 'all 0.3s',
                  background: isDark ? '#161b22' : '#fff',
                  border: isDark ? '1px solid #30363d' : '1px solid #e8e8e8',
                }}
              >
                {/* Card Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  
                  <Button
                    type="text"
                    danger
                    size="small"onClick={(e) => handleDeleteClick(dashboard.name, e)}
                  />
                </div>

                {/* Dashboard Title */}
                <h3 style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '16px', 
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: isDark ? '#e6edf3' : '#000'
                }}>
                  {dashboard.title || dashboard.name}
                </h3>

                {/* Dashboard Name (if different from title) */}
                {dashboard.title && dashboard.title !== dashboard.name && (
                  <p style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '12px', 
                    color: isDark ? '#adbac7' : '#8c8c8c',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {dashboard.name}
                  </p>
                )}

                {/* Metadata */}
                <div style={{ marginTop: '12px', fontSize: '12px', color: isDark ? '#adbac7' : '#8c8c8c' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                    
                    <span>{formatDate(dashboard.updatedAt)}</span>
                  </div>
                  {dashboard.version && (
                    <Tag color="blue" style={{ margin: '4px 0 0 0' }}>
                      v{dashboard.version}
                    </Tag>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Dashboard"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p style={{ color: isDark ? '#e6edf3' : undefined }}>Are you sure you want to delete <strong>"{dashboardToDelete}"</strong>?</p>
        <p style={{ color: '#ff4d4f' }}>This action cannot be undone.</p>
      </Modal>

      {/* Create Dashboard Modal */}
      <Modal
        title="Create New Dashboard"
        open={createModalVisible}
        onOk={handleCreateConfirm}
        onCancel={() => {
          setCreateModalVisible(false);
          setNewDashboardName('');
        }}
        okText="Create"
      >
        <p style={{ marginBottom: '12px', color: isDark ? '#e6edf3' : undefined }}>Enter a name for your new dashboard:</p>
        <Input
          placeholder="e.g., Lambda Monitoring"
          value={newDashboardName}
          onChange={(e) => setNewDashboardName(e.target.value)}
          onPressEnter={handleCreateConfirm}
          autoFocus
        />
      </Modal>
    </div>
  );
};

export default DashboardList;